import { useState, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Loader2, Save, X, UploadCloud, Image as ImageIcon } from "lucide-react";
import { adminApi, formatApiError } from "@/lib/adminApi";
import { toast } from "sonner";

const EMPTY = {
  title: "",
  slug: "",
  category: "Insights",
  read_time: "4 min read",
  cover_image: "",
  excerpt: "",
  status: "draft",
  content: "",
};

const inputClass =
  "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-blue/40 focus:border-brand-blue transition";

export const PostEditor = ({ post, onClose, onSaved }) => {
  const isEdit = Boolean(post?.id);
  const [form, setForm] = useState(post ? { ...EMPTY, ...post } : EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    const data = new FormData();
    data.append("file", file);
    setUploading(true);
    try {
      const res = await adminApi.post("/admin/blog/upload-cover", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, cover_image: res.data.url }));
      toast.success("Cover image uploaded.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    uploadFile(file);
  };

  const save = async (status) => {
    if (!form.title.trim() || !form.excerpt.trim()) {
      toast.error("Title and excerpt are required.");
      return;
    }
    const payload = { ...form, status };
    setSaving(true);
    try {
      if (isEdit) {
        await adminApi.put(`/admin/blog/posts/${post.id}`, payload);
      } else {
        await adminApi.post(`/admin/blog/posts`, payload);
      }
      toast.success(
        status === "published" ? "Post published." : "Draft saved."
      );
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4">
      <div
        data-testid="post-editor"
        className="w-full max-w-5xl my-8 rounded-2xl bg-white shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 className="font-heading font-700 text-lg text-brand-navy">
            {isEdit ? "Edit post" : "New post"}
          </h2>
          <button
            data-testid="editor-close"
            onClick={onClose}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"
            aria-label="Close editor"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Title
              </label>
              <input data-testid="editor-title" className={inputClass} value={form.title} onChange={set("title")} placeholder="Post title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                  Category
                </label>
                <input data-testid="editor-category" className={inputClass} value={form.category} onChange={set("category")} />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                  Read time
                </label>
                <input data-testid="editor-readtime" className={inputClass} value={form.read_time} onChange={set("read_time")} />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Slug (optional — auto from title)
              </label>
              <input data-testid="editor-slug" className={inputClass} value={form.slug} onChange={set("slug")} placeholder="my-post-slug" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Cover image
              </label>
              <div
                data-testid="editor-cover-dropzone"
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                className={`cursor-pointer rounded-lg border-2 border-dashed px-4 py-5 text-center transition-colors ${
                  dragOver ? "border-brand-blue bg-brand-blue/5" : "border-slate-300 hover:border-brand-blue/60"
                }`}
              >
                <input
                  ref={fileRef}
                  data-testid="editor-cover-file"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => uploadFile(e.target.files?.[0])}
                />
                {uploading ? (
                  <span className="inline-flex items-center gap-2 text-sm text-slate-500">
                    <Loader2 className="h-4 w-4 animate-spin" /> Uploading...
                  </span>
                ) : form.cover_image ? (
                  <div className="flex items-center gap-3">
                    <img src={form.cover_image} alt="" className="h-14 w-20 object-cover rounded-md" />
                    <span className="inline-flex items-center gap-1.5 text-sm text-brand-blue">
                      <UploadCloud className="h-4 w-4" /> Replace image
                    </span>
                  </div>
                ) : (
                  <span className="inline-flex items-center gap-2 text-sm text-slate-500">
                    <ImageIcon className="h-4 w-4" /> Drag & drop or click to upload
                  </span>
                )}
              </div>
              <input
                data-testid="editor-cover"
                className={`${inputClass} mt-2`}
                value={form.cover_image}
                onChange={set("cover_image")}
                placeholder="…or paste an image URL"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Excerpt
              </label>
              <textarea data-testid="editor-excerpt" rows={3} className={`${inputClass} resize-y`} value={form.excerpt} onChange={set("excerpt")} placeholder="Short summary shown on cards" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Content (Markdown)
              </label>
              <textarea data-testid="editor-content" rows={16} className={`${inputClass} font-mono resize-y`} value={form.content} onChange={set("content")} placeholder="## Heading&#10;&#10;Write in markdown..." />
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
              Live preview
            </p>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 h-[540px] overflow-y-auto">
              {form.cover_image && (
                <img src={form.cover_image} alt="" className="w-full h-40 object-cover rounded-lg mb-4" />
              )}
              <h1 className="font-heading font-800 text-2xl text-brand-navy">
                {form.title || "Post title"}
              </h1>
              <div className="mt-4 prose-sm">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h2: (p) => <h2 className="mt-5 mb-2 font-heading font-700 text-lg text-brand-navy" {...p} />,
                    h3: (p) => <h3 className="mt-4 mb-1 font-heading font-600 text-base text-brand-navy" {...p} />,
                    p: (p) => <p className="mt-3 text-sm leading-relaxed text-slate-600" {...p} />,
                    ul: (p) => <ul className="mt-3 list-disc pl-5 text-sm text-slate-600 space-y-1" {...p} />,
                    ol: (p) => <ol className="mt-3 list-decimal pl-5 text-sm text-slate-600 space-y-1" {...p} />,
                    a: (p) => <a className="text-brand-blue underline" {...p} />,
                    strong: (p) => <strong className="font-semibold text-brand-navy" {...p} />,
                  }}
                >
                  {form.content || "_Nothing to preview yet._"}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <button data-testid="editor-cancel" onClick={onClose} className="px-5 py-2.5 rounded-lg text-slate-600 hover:bg-slate-100 font-medium text-sm">
            Cancel
          </button>
          <button data-testid="editor-save-draft" onClick={() => save("draft")} disabled={saving || uploading} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 font-medium text-sm disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save draft
          </button>
          <button data-testid="editor-publish" onClick={() => save("published")} disabled={saving || uploading} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-brand-navy text-white hover:bg-brand-navy-dark font-medium text-sm disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Publish
          </button>
        </div>
      </div>
    </div>
  );
};
